import { Box, Divider, Modal, Typography } from "@mui/material";
import {
  useGetBreakdownProcessV1,
  useGetBreakdownProcessV2,
  useCreateMaterialProcessV2,
  useUpdateMaterialProcessV2,
  useDeleteMaterialProcessV2,
} from "@/hooks/breakdownHooks";
import CloseButton from "@/components/buttons/CloseButton";
import { InformationModal } from "@/styles/globalstyles";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import ProcessList from "./ProcessList";
import EditableProcessList from "./EditableProcessList";
import {
  useGetMaterial2ndTierProcesses,
  useGetMaterialOtherProcesses,
} from "@/hooks/masterHook";
import { type Master } from "@/api/masterService";
import { useState } from "react";
import { ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";

interface ManageMaterialProcessModalProps {
  open: boolean;
  handleClose: () => void;
  supplierCode?: string;
  partId?: number;
  version: "v1" | "v2";
  canEdit: boolean;
}

const ManageMaterialProcessModal = ({
  open,
  handleClose,
  supplierCode,
  partId,
  version,
  canEdit,
}: ManageMaterialProcessModalProps) => {
  const [disabled, setDisabled] = useState(false);
  const {
    data: breakdownProcessV1,
    isLoading: isLoadingBreakdownProcessV1,
    isError: isErrorBreakdownProcessV1,
  } = useGetBreakdownProcessV1(supplierCode || "");

  const {
    data: breakdownProcessV2,
    isLoading: isLoadingBreakdownProcessV2,
    isError: isErrorBreakdownProcessV2,
  } = useGetBreakdownProcessV2(partId || 0);

  const {
    data: material2ndTierProcesses,
    isLoading: isLoadingMaterial2ndTierProcesses,
    isError: isErrorMaterial2ndTierProcesses,
  } = useGetMaterial2ndTierProcesses(
    { skip: 0, limit: 1000, active_only: true },
    true,
  );

  const {
    data: materialOtherProcesses,
    isLoading: isLoadingMaterialOtherProcesses,
    isError: isErrorMaterialOtherProcesses,
  } = useGetMaterialOtherProcesses(
    { skip: 0, limit: 1000, active_only: true },
    true,
  );

  const {
    mutate: createMaterialProcessV2,
    isPending: isCreatingMaterialProcessV2,
  } = useCreateMaterialProcessV2();

  const {
    mutate: updateMaterialProcessV2,
    isPending: isUpdatingMaterialProcessV2,
  } = useUpdateMaterialProcessV2();

  const {
    mutate: deleteMaterialProcessV2,
    isPending: isDeletingMaterialProcessV2,
  } = useDeleteMaterialProcessV2();

  useQueryHandler({
    isFetching:
      isLoadingBreakdownProcessV1 ||
      isLoadingBreakdownProcessV2 ||
      isLoadingMaterial2ndTierProcesses ||
      isLoadingMaterialOtherProcesses ||
      isCreatingMaterialProcessV2 ||
      isUpdatingMaterialProcessV2 ||
      isDeletingMaterialProcessV2,
    isError:
      isErrorBreakdownProcessV1 ||
      isErrorBreakdownProcessV2 ||
      isErrorMaterial2ndTierProcesses ||
      isErrorMaterialOtherProcesses,
  });

  const onClose = () => {
    handleClose();
    setDisabled(false);
  };

  const handleAddMaterial2ndTierProcess = async (process: {
    name: string;
    master_process_id: number;
  }) => {
    createMaterialProcessV2(
      {
        partId: partId || 0,
        params: {
          material_second_tier_processes: [
            {
              ms_material_second_tier_process_id: process.master_process_id,
            },
          ],
          material_other_processes: [],
        },
      },
      {
        onError: (error) => {
          console.error("Error creating material process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleAddMaterialOtherProcess = async (process: {
    name: string;
    master_process_id: number;
  }) => {
    createMaterialProcessV2(
      {
        partId: partId || 0,
        params: {
          material_second_tier_processes: [],
          material_other_processes: [
            {
              ms_material_other_process_id: process.master_process_id,
            },
          ],
        },
      },
      {
        onError: (error) => {
          console.error("Error creating material process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleEditMaterial2ndTierProcess = async (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    updateMaterialProcessV2(
      {
        partId: partId || 0,
        params: {
          material_second_tier_processes: [
            {
              part_material_second_tier_process_id: process.id,
              ms_material_second_tier_process_id: process.master_process_id,
            },
          ],
          material_other_processes: [],
        },
      },
      {
        onError: (error) => {
          console.error("Error updating material process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleEditMaterialOtherProcess = async (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    updateMaterialProcessV2(
      {
        partId: partId || 0,
        params: {
          material_second_tier_processes: [],
          material_other_processes: [
            {
              part_material_other_process_id: process.id,
              ms_material_other_process_id: process.master_process_id,
            },
          ],
        },
      },
      {
        onError: (error) => {
          console.error("Error updating material process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleDeleteMaterial2ndTierProcess = async (processId: number) => {
    deleteMaterialProcessV2(
      {
        partId: partId || 0,
        params: {
          material_second_tier_processes: [
            { part_material_second_tier_process_id: processId },
          ],
          material_other_processes: [],
        },
      },
      {
        onError: (error) => {
          console.error("Error deleting material process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleDeleteMaterialOtherProcess = async (processId: number) => {
    deleteMaterialProcessV2(
      {
        partId: partId || 0,
        params: {
          material_second_tier_processes: [],
          material_other_processes: [
            { part_material_other_process_id: processId },
          ],
        },
      },
      {
        onError: (error) => {
          console.error("Error deleting material process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...InformationModal,
          minHeight: "400px",
          maxHeight: "90vh",
          width: "70vw",
        }}
      >
        <CloseButton onClick={onClose} />
        <Typography variant="h2">{"จัดการ Material Process"}</Typography>
        <Divider sx={{ my: 1 }} />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} p={1} pt={2}>
          {version === "v1" && (
            <>
              <ProcessList
                title="2nd Tier Process"
                data={
                  breakdownProcessV1?.second_tier_process?.map((process) => ({
                    id: String(process.id),
                    name: process.name,
                  })) || []
                }
              />
              <ProcessList
                title="Other Process"
                data={
                  breakdownProcessV1?.other_process?.map((process) => ({
                    id: String(process.id),
                    name: process.name,
                  })) || []
                }
              />
            </>
          )}
          {version === "v2" && (
            <>
              <EditableProcessList
                title="2nd Tier Process"
                data={
                  breakdownProcessV2?.second_tier_process?.map((process) => ({
                    id: process.id,
                    name: process.name,
                    master_process_id: process.master_process_id || 0,
                  })) || []
                }
                options={
                  material2ndTierProcesses?.map((process: Master) => ({
                    value: process.id,
                    label: process.name,
                  })) || []
                }
                handleAddProcess={async (process) => {
                  handleAddMaterial2ndTierProcess(process);
                }}
                handleEditProcess={async (process) => {
                  handleEditMaterial2ndTierProcess(process);
                }}
                handleDeleteProcess={async (processId) => {
                  handleDeleteMaterial2ndTierProcess(processId);
                }}
                disabled={disabled || !canEdit}
                setDisabled={setDisabled}
              />
              <EditableProcessList
                title="Other Process"
                data={
                  breakdownProcessV2?.other_process?.map((process) => ({
                    id: process.id,
                    name: process.name,
                    master_process_id: process.master_process_id || 0,
                  })) || []
                }
                options={
                  materialOtherProcesses?.map((process: Master) => ({
                    value: process.id,
                    label: process.name,
                  })) || []
                }
                handleAddProcess={async (process) => {
                  handleAddMaterialOtherProcess(process);
                }}
                handleEditProcess={async (process) => {
                  handleEditMaterialOtherProcess(process);
                }}
                handleDeleteProcess={async (processId) => {
                  handleDeleteMaterialOtherProcess(processId);
                }}
                disabled={disabled || !canEdit}
                setDisabled={setDisabled}
              />
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ManageMaterialProcessModal;
